import {
  TProgressiveRule,
  TProgressiveTier,
  getDisplayableTiers,
  formatTierQuantityLabel
} from '@cannect/services/resources/progressive-discounts'
import { formatReal } from '@cannect/utils/formatters'
import { useState } from 'react'

type Props = {
  tiers: TProgressiveTier[]
  rule?: TProgressiveRule
  onSelectTier?: (quantity: number) => void
  /** Preço real do produto na PDP (usado para calcular unitFinal por faixa). */
  productPrice?: number
}

export const ProgressiveDiscountTiers = ({ tiers, rule, onSelectTier, productPrice }: Props) => {
  const displayableTiers = getDisplayableTiers(tiers)
  const [selectedMinQty, setSelectedMinQty] = useState<number | null>(null)

  if (!displayableTiers.length) return null

  const isShipping = rule?.benefit_target === 'shipping'

  const handleTierClick = (minQty: number) => {
    setSelectedMinQty(minQty)
    onSelectTier?.(minQty)
  }

  return (
    <div className="mt-4 w-full rounded-2xl border border-primary-100 bg-primary-50/40 p-4">

      {onSelectTier && (
        <p className="mb-2 text-xs text-neutral-500">Clique em uma faixa para selecionar a quantidade.</p>
      )}

      <div className="flex flex-col gap-2">
        {displayableTiers.map((tier) => {
          const isSelected = selectedMinQty === tier.min_quantity
          const pct = Number(tier.discount_percent) || 0
          // Usa o preço real do produto (não o unit_final_price da API, que é baseado em catálogo)
          const unitFinal = productPrice && pct > 0 ? +(productPrice * (1 - pct / 100)).toFixed(2) : null
          const totalFinal = unitFinal ? +(unitFinal * tier.min_quantity).toFixed(2) : null

          const Wrapper = onSelectTier ? 'button' : 'div'
          const wrapperProps = onSelectTier
            ? {
                type: 'button' as const,
                onClick: () => handleTierClick(tier.min_quantity)
              }
            : {}

          return (
            <Wrapper
              key={tier.min_quantity}
              {...wrapperProps}
              className={[
                'flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors',
                onSelectTier ? 'cursor-pointer' : '',
                isSelected
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400'
                  : 'border-primary-100 bg-white hover:border-primary-300 hover:bg-primary-50/60'
              ]
                .filter(Boolean)
                .join(' ')}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-neutral-600">
                  {isShipping ? `a partir de ${tier.min_quantity} un` : formatTierQuantityLabel(tier)}
                </span>
                <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                  {tier.discount_percent}% OFF{isShipping ? ' no Frete' : ''}
                </span>
              </div>
              {unitFinal !== null && (
                <div className="text-right">
                  <p className="text-sm font-semibold text-neutral-800">
                    {(formatReal(unitFinal) as string) || `R$ ${unitFinal}`}
                    <span className="text-xs font-normal text-neutral-500"> /un</span>
                  </p>
                  {totalFinal !== null && (
                    <p className="text-xs text-neutral-500">
                      Total: {(formatReal(totalFinal) as string) || `R$ ${totalFinal}`}
                    </p>
                  )}
                </div>
              )}
            </Wrapper>
          )
        })}
      </div>
    </div>
  )
}
